import React from "react";
import { Box, IconButton, Typography, Tooltip } from "@mui/material";
import CustomAutocomplete from "../components/common/ui/CustomAutocomplete";
import { useAuth } from "../context/AuthContext";
import { USER } from "../constants/userCred";
import SpeakerTimestamp from "./SpeakerTimestamp";
import CopyIcon from "@mui/icons-material/FileCopy";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { ISummaryProps } from "../interfaces/home/ISummary";
import { AGENDA_DATA } from "../constants/agenda";
import { RestfulUrls } from "../constants/restfulUrls";
import { hasPermission } from "../utils/permissionUtil";
import { PERMISSION } from "../constants/userPermission";
import { useSelector } from "react-redux";
import { IUserDetailsPermissionState } from "../interfaces/store/IUserDetailsPermissionState";

const Summary: React.FC<ISummaryProps> = ({
  speaker,
  index,
  agendaIndex,
  scrollToText,
  handleNavigateSpeechId,
  countryOptions,
  handleCountryChange,
  handleCopy,
  handleOpenEditSummModal,
  handleSummaryEdit,
  toggleTranscriptionShowMore,
  agendaID,
  handleDeleteInterventionSummary,
}) => {
  const { userRole } = useAuth();
  const hasSummary = speaker.summary && speaker.summary.length > 0;
  const addLabelURL = `${RestfulUrls.BASE_URL}${RestfulUrls.ADD_INTERVENTION_LABEL}`;

  const userPermissions = useSelector(
    (state: { userDetailsPermission: IUserDetailsPermissionState }) =>
      state.userDetailsPermission.rolePermission
  );

  // RBAC: Determine if user can edit this summary
  // Admin can edit any summary
  // Rapporteur can only edit summaries they created
  const canEditSummary = React.useMemo(() => {
    if (!userRole) return false;
    
    // Admin and Super Admin can edit any summary
    if (userRole === USER.USER_ROLE.ADMIN || userRole === USER.USER_ROLE.SUPER_ADMIN) {
      return hasPermission(
        userPermissions,
        PERMISSION?.MODULE?.MEETING_TRANSCRIPTION_PAGE,
        PERMISSION?.ACTION?.EDIT_INTERVENTION_SUMMARY
      );
    }
    
    // Rapporteur can only edit summaries they created (role-based only)
    if (userRole === USER.USER_ROLE.RAPUTER || userRole === "RAPPORTEUR") {
      // CRITICAL: First check if summary was created by Admin or Super Admin
      // If createdBy is "ADMIN" or "SUPER_ADMIN", Rapporteur cannot edit
      const createdByLower = (speaker.createdBy || "").toLowerCase();
      if (createdByLower === "admin" || createdByLower === "super_admin") {
        return false; // Rapporteur cannot edit Admin/Super Admin summaries
      }
      
      // Check if createdBy matches userRole ONLY (role-based check)
      const userRoleLower = (userRole || "").toLowerCase();
      
      // Allow only if createdBy matches userRole
      const isCreatedByCurrentUser = createdByLower === userRoleLower;
      
      return (
        isCreatedByCurrentUser &&
        hasPermission(
          userPermissions,
          PERMISSION?.MODULE?.MEETING_TRANSCRIPTION_PAGE,
          PERMISSION?.ACTION?.EDIT_INTERVENTION_SUMMARY
        )
      );
    }
    
    return false;
  }, [userRole, speaker.createdBy, userPermissions]);

  // RBAC: Determine if user can delete this summary
  // Admin can delete any summary
  // Rapporteur can only delete summaries they created
  const canDeleteSummary = React.useMemo(() => {
    // Must have a summary to delete
    if (!hasSummary) {
      return false;
    }
    
    // Must have a user role
    if (!userRole) {
      return false;
    }
    
    // Admin and Super Admin can delete any summary (they have all permissions by default)
    if (userRole === USER.USER_ROLE.ADMIN || userRole === USER.USER_ROLE.SUPER_ADMIN) {
      return true;
    }
    
    // For other roles, check if user has delete permission
    const hasDeletePermission = hasPermission(
      userPermissions,
      PERMISSION?.MODULE?.MEETING_TRANSCRIPTION_PAGE,
      PERMISSION?.ACTION?.DELETE_INTERVENTION_SUMMARY
    );
    
    if (!hasDeletePermission) {
      return false;
    }
    
    // Rapporteur can only delete summaries they created (role-based only)
    if (userRole === USER.USER_ROLE.RAPUTER || userRole === "RAPPORTEUR") {
      // CRITICAL: First check if summary was created by Admin or Super Admin
      // If createdBy is "ADMIN" or "SUPER_ADMIN", Rapporteur cannot delete
      const createdByLower = (speaker.createdBy || "").toLowerCase();
      if (createdByLower === "admin" || createdByLower === "super_admin") {
        return false; // Rapporteur cannot delete Admin/Super Admin summaries
      }
      
      // Check if createdBy matches userRole ONLY (role-based check)
      const userRoleLower = (userRole || "").toLowerCase();
      
      // Allow only if createdBy matches userRole
      return createdByLower === userRoleLower;
    }
    
    return false;
  }, [userRole, speaker.createdBy, userPermissions, hasSummary]);

  return (
    <Box key={index} id={`${speaker?.speech_id}_${speaker?.country}`}>
      {hasSummary && (
        <Box className="summary-container">
          {speaker.isCountryEditing ? (
            <Box className="country-option-container">
              {speaker.country ? (
                <span
                  onClick={() => {
                    if (speaker?.speech_id) {
                      scrollToText(speaker.speech_id);
                      handleNavigateSpeechId(speaker.speech_id);
                    }
                  }}
                >
                  {speaker.country || "No country selected"}
                </span>
              ) : (
                <CustomAutocomplete
                  options={countryOptions}
                  disabled={
                    !(
                      // Admin and Super Admin can always edit
                      userRole === USER.USER_ROLE.ADMIN ||
                      userRole === USER.USER_ROLE.SUPER_ADMIN ||
                      // Check if current user created this summary (role-based only)
                      (() => {
                        if (!speaker.createdBy || !userRole) return false;
                        const createdByLower = (speaker.createdBy || "").toLowerCase();
                        
                        // CRITICAL: Rapporteur cannot edit summaries created by Admin/Super Admin
                        if (createdByLower === "admin" || createdByLower === "super_admin") {
                          return false;
                        }
                        
                        // Check if createdBy matches userRole ONLY (role-based check)
                        const userRoleLower = (userRole || "").toLowerCase();
                        return createdByLower === userRoleLower;
                      })()
                    )
                  }
                  addUrl={addLabelURL}
                  value={
                    speaker.country
                      ? { label: speaker.country, value: speaker.country }
                      : { label: "", value: "" }
                  }
                  onChange={(newValue) => {
                    if (newValue) {
                      handleCountryChange(
                        newValue,
                        index,
                        agendaIndex ?? undefined
                      );
                    }
                  }}
                  label="Select or Add Country"
                />
              )}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {hasPermission(
                  userPermissions,
                  PERMISSION.MODULE?.MEETING_TRANSCRIPTION_PAGE,
                  PERMISSION.ACTION?.COPY_SUMMARY
                ) && (
                  <>
                    <IconButton
                      onClick={() => handleCopy(speaker.country + " : " + speaker.summary || "")}
                      className="copy-icon"
                    >
                      <CopyIcon />
                    </IconButton>
                  </>
                )}

                {canEditSummary && (
                  <Tooltip title="Edit Intervention Summary">
                    <EditIcon
                      onClick={() =>
                        handleOpenEditSummModal(
                          index,
                          agendaIndex,
                          speaker.summary || "",
                          speaker.country || "",
                          AGENDA_DATA.EDIT_SUMMARY_TYPE.INTERVENTION_SUMMARY
                        )
                      }
                      sx={{
                        fontSize: "13px",
                        cursor: "pointer",
                        ml: "5px",
                        color: "#1976d2",
                        "&:hover": {
                          color: "#1565c0",
                        },
                      }}
                    />
                  </Tooltip>
                )}
                {canDeleteSummary && handleDeleteInterventionSummary && (
                  <Tooltip title="Delete Intervention Summary">
                    <IconButton
                      onClick={() => {
                        if (speaker.speech_id) {
                          handleDeleteInterventionSummary(speaker.speech_id, index, agendaIndex);
                        }
                      }}
                      size="small"
                      sx={{
                        ml: "5px",
                        color: "#d32f2f",
                        "&:hover": {
                          color: "#c62828",
                          backgroundColor: "rgba(211, 47, 47, 0.04)",
                        },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: "13px" }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          ) : (
            <Box className="country-option-container">
              <Typography
                variant="body2"
                className="country-name"
                onClick={() => {
                  handleSummaryEdit(index);
                }}
              >
                {speaker?.country} (intervention summary)
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {hasPermission(
                  userPermissions,
                  PERMISSION.MODULE?.MEETING_TRANSCRIPTION_PAGE,
                  PERMISSION.ACTION?.COPY_SUMMARY
                ) && (
                  <IconButton
                    onClick={() => handleCopy(speaker.country + " : " + speaker.summary || "")}
                    className="copy-icon"
                    size="small"
                  >
                    <CopyIcon />
                  </IconButton>
                )}
                {canEditSummary && (
                  <Tooltip title="Edit Intervention Summary">
                    <EditIcon
                      onClick={() =>
                        handleOpenEditSummModal(
                          index,
                          agendaIndex,
                          speaker.summary || "",
                          speaker.country || "",
                          AGENDA_DATA.EDIT_SUMMARY_TYPE.INTERVENTION_SUMMARY
                        )
                      }
                      sx={{
                        fontSize: "13px",
                        cursor: "pointer",
                        color: "#1976d2",
                        "&:hover": {
                          color: "#1565c0",
                        },
                      }}
                    />
                  </Tooltip>
                )}
                {canDeleteSummary && handleDeleteInterventionSummary && (
                  <Tooltip title="Delete Intervention Summary">
                    <IconButton
                      onClick={() => {
                        if (speaker.speech_id) {
                          handleDeleteInterventionSummary(speaker.speech_id, index, agendaIndex);
                        }
                      }}
                      size="small"
                      sx={{
                        ml: "5px",
                        color: "#d32f2f",
                        "&:hover": {
                          color: "#c62828",
                          backgroundColor: "rgba(211, 47, 47, 0.04)",
                        },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: "13px" }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          )}
          <Box
            onClick={() => {
              if (speaker?.speech_id) {
                scrollToText(speaker.speech_id);
                handleNavigateSpeechId(speaker.speech_id);
              }
            }}
          >
            <SpeakerTimestamp
              timestamp={
                speaker?.summaryTimestamp
                  ? speaker?.summaryTimestamp
                  : speaker?.timestamp
              }
              timeClass="intervention-overview-time"
            />
          </Box>

          {/* Summary Text */}
          <Typography
            variant="body2"
            className="summary-text"
            onClick={() => {
              if (speaker?.speech_id) {
                scrollToText(speaker.speech_id);
                handleNavigateSpeechId(speaker.speech_id);
              }
            }}
            title="Click to navigate to the transcription"
          >
            {speaker?.isExpanded
              ? speaker.summary ?? ""
              : `${(speaker.summary ?? "").slice(0, 100)}${
                  (speaker?.summary ?? "").length > 100 ? "..." : ""
                }`}
          </Typography>
          {speaker?.isExpanded ? (
            <Typography
              variant="body2"
              className="expand-showless"
              onClick={() =>
                toggleTranscriptionShowMore(
                  agendaID ?? null,
                  speaker?.speech_id,
                  AGENDA_DATA.SUMMARY_SHOW_TYPE.SHOW_LESS
                )
              }
            >
              Show Less
            </Typography>
          ) : (
            <Typography
              variant="body2"
              className="expand-showless"
              onClick={() =>
                toggleTranscriptionShowMore(
                  agendaID ?? null,
                  speaker?.speech_id,
                  AGENDA_DATA.SUMMARY_SHOW_TYPE.EXPAND
                )
              }
            >
              Expand
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Summary;
